import {
  BottomSheetContainer,
  Colors,
  ColorValues,
  createUseBottomSheetModal,
  Flex,
  HEADER_HEIGHT,
  HorizontalSpacing,
  IconCheck,
  IconDownload,
  List,
  Spinner,
  Tag,
  Typography,
  useSafeArea,
  useScreenSize,
  VerticalSpacing,
} from '@banhala/ui-kit';
import { useCallback, useMemo } from 'react';
import { useGoodsCouponList } from '../queries/useGoodsCouponList';
import type { GoodsCoupon } from 'shared/src/api';
import { bridge, localizeNumber } from 'shared/src/utils';
import { HTMLText } from 'shared/src/component/HTMLText';
import { useOnPressGoodsCouponRedeemAll } from '../hooks/useOnPressGoodsCouponRedeemAll';
import { usePostRedeemCoupon } from '../../../queries/usePostRedeemCoupon';
import { useLogViewCouponListModal } from '../hooks/useLogViewCouponListModal';
import { useLogDownloadCoupon } from '../hooks/useLogDownloadCoupon';
import { useFeatureFlagValue } from 'shared/src/hooks';
import { useMyInfo } from 'shared/src/queries';

const DOCKED_PRIMARY_BUTTON_HEIGHT = 56 + 12 * 2;

const CouponApplicableType = {
  DEFAULT: 1,
  STACKABLE: 2,
  NOT_STACKABLE: 3,
};

type CouponSectionViewItem =
  | { type: 'title'; title: string }
  | { type: 'coupon'; item: GoodsCoupon };

const CouponItemView: React.FC<{
  coupon: GoodsCoupon;
}> = props => {
  const { mutateAsync: postRedeemCoupon, isPending: loading } =
    usePostRedeemCoupon();
  const { data: myInfo } = useMyInfo();

  const logDownloadCoupon = useLogDownloadCoupon();

  const onPressRedeemCoupon = useCallback(async () => {
    if (myInfo?.is_anonymous) {
      bridge.assertLogin({});
      setTimeout(() => {
        bridge.toast('로그인이나 회원가입이 필요해요');
      }, 200);
      return;
    }

    try {
      await postRedeemCoupon({ sno: props.coupon.sno });
      bridge.toast('쿠폰이 발급되었습니다');
      logDownloadCoupon({ coupon: props.coupon, isDownloadAll: false });
    } catch (ex) {
      const error = ex as Error;
      bridge.toast(error.message);
    }
  }, [logDownloadCoupon, myInfo, postRedeemCoupon, props.coupon]);

  return (
    <Flex.Row
      padding={{ horizontal: 24, vertical: 24 }}
      shadowStep={'small'}
      backgroundColor={'white'}
      borderRadius={8}
    >
      <Flex flex={1}>
        <Flex.Row gap={8} alignItems={'center'}>
          <Typography type={'h2'} color={'content_blue_primary'}>
            {props.coupon.discount_type === 'PERCENT'
              ? `${props.coupon.discount_percent}%`
              : `${localizeNumber(props.coupon.discount_price)}원`}
          </Typography>
          {props.coupon.applicable_type ===
            CouponApplicableType.NOT_STACKABLE && (
            <Tag size={'xsmall'} color={'pink'} type={'secondary'}>
              중복 사용 불가
            </Tag>
          )}
        </Flex.Row>
        <VerticalSpacing size={6} />
        <Typography type={'subtitle2'}>{props.coupon.name}</Typography>
        <VerticalSpacing size={6} />
        {props.coupon.conditions_with_markup.split('\n').map(condition => (
          <Flex margin={{ top: 6 }} key={condition}>
            <HTMLText type="body4" color="content_tertiary" numberOfLines={2}>
              {condition}
            </HTMLText>
          </Flex>
        ))}
        <VerticalSpacing size={6} />
      </Flex>
      <HorizontalSpacing size={16} />
      <Flex.Center>
        {props.coupon.is_issued ? (
          <Flex.Center gap={4}>
            <IconCheck fill={Colors['content_tertiary']} size={24} />
            <Typography type={'subtitle6'} color={'content_tertiary'}>
              발급완료
            </Typography>
          </Flex.Center>
        ) : (
          <Flex.Center
            width={52}
            height={52}
            borderRadius={26}
            backgroundColorValue={ColorValues.blue5}
            onPress={onPressRedeemCoupon}
          >
            {loading ? (
              <Spinner color={'content_blue_primary'} size={24} />
            ) : (
              <IconDownload fill={Colors['content_blue_primary']} size={24} />
            )}
          </Flex.Center>
        )}
      </Flex.Center>
    </Flex.Row>
  );
};
const CouponGap = () => {
  return <VerticalSpacing size={16} />;
};

export const useGoodsCouponListBottomSheet = createUseBottomSheetModal(
  ({ cancel }) => {
    const { height: screenHeight } = useScreenSize();
    const { top, bottom } = useSafeArea();
    const { data: coupons, isLoading: loadingCouponList } =
      useGoodsCouponList();
    const enableStackableFlag = useFeatureFlagValue('stackable-coupon');

    const { action: redeemAllCouponAction, loading: loadingRedeemAll } =
      useOnPressGoodsCouponRedeemAll(
        coupons?.allCoupons?.filter(coupon => !coupon.is_issued) ?? [],
      );
    useLogViewCouponListModal(coupons);

    const groupedCouponList = useMemo<CouponSectionViewItem[]>(() => {
      if (!coupons) return [];
      const couponList: CouponSectionViewItem[] = [];
      if (enableStackableFlag) {
        if (coupons.marketCoupons.length > 0) {
          couponList.push({
            title: `중복 사용 가능 쿠폰 ${coupons.marketCoupons.length}장`,
            type: 'title' as const,
          });
          couponList.push(
            ...coupons.marketCoupons
              .filter(coupon => coupon.is_enabled)
              .map(coupon => ({
                type: 'coupon' as const,
                item: coupon,
              })),
          );
        }

        if (coupons.cartCoupons.length > 0) {
          couponList.push({
            title: `장바구니 쿠폰 ${coupons.cartCoupons.length}장`,
            type: 'title' as const,
          });

          couponList.push(
            ...coupons.cartCoupons
              .filter(coupon => coupon.is_enabled)
              .map(coupon => ({
                type: 'coupon' as const,
                item: coupon,
              })),
          );
        }
      } else {
        couponList.push(
          ...coupons.allCoupons
            .filter(coupon => coupon.is_enabled)
            .map(coupon => ({
              type: 'coupon' as const,
              item: coupon,
            })),
        );
      }

      return couponList;
    }, [coupons, enableStackableFlag]);

    const isAllCouponIssued = useMemo(() => {
      if (!coupons) return true;

      return (
        coupons.allCoupons.filter(coupon => !coupon.is_issued).length === 0
      );
    }, [coupons]);

    const contentHeight = useMemo(() => {
      return (
        (screenHeight -
          HEADER_HEIGHT -
          top -
          bottom -
          DOCKED_PRIMARY_BUTTON_HEIGHT) *
        0.86
      );
    }, [bottom, screenHeight, top]);

    return (
      <BottomSheetContainer
        title={'쿠폰'}
        maxHeight={screenHeight}
        bottomDocked={{
          type: 'normal',
          paired: false,
          color: 'blue',
          stroke: true,
          button: {
            state:
              loadingCouponList || loadingRedeemAll
                ? 'loading'
                : isAllCouponIssued
                  ? 'disabled'
                  : undefined,
            title: isAllCouponIssued ? '다운로드 완료' : '모두 다운받기',
            icon: isAllCouponIssued ? undefined : <IconDownload />,
            outline: true,

            onPress: redeemAllCouponAction,
          },
        }}
        onPressClose={cancel}
      >
        {loadingCouponList ? (
          <Flex.Center height={contentHeight}>
            <Spinner color={'content_blue_primary'} />
          </Flex.Center>
        ) : (
          <Flex height={contentHeight}>
            <List<CouponSectionViewItem>
              style={{ flex: 1 }}
              contentContainerStyle={{
                paddingVertical: 12,
                paddingHorizontal: 16,
              }}
              data={groupedCouponList}
              renderItem={({ item }) => {
                if (item.type === 'coupon') {
                  return <CouponItemView coupon={item.item} />;
                }

                if (item.type === 'title') {
                  return (
                    <Flex padding={{ top: 20 }}>
                      <Typography
                        type={'subtitle2'}
                        color={'content_secondary'}
                      >
                        {item.title}
                      </Typography>
                    </Flex>
                  );
                }
                return null;
              }}
              ItemSeparatorComponent={CouponGap}
            />
          </Flex>
        )}
      </BottomSheetContainer>
    );
  },
);
